/**
 * copyOutbox — la file d'envoi de la copie d'une activité : chaque photo, et l'oral, un par un.
 *
 * La file commune du moteur (`engine/shared/outbox`) : un état visible par pièce, la survie à l'app
 * tuée (la copie durable du fichier, `activity-copy-outbox`), un envoi à la fois, « Réessayer » par
 * pièce. Une pièce part en deux temps, comme dans l'ancien lot (`useRequestsBatch` dans
 * `ActivityScreen.js`) :
 *  1. **S3** : `POST /vapor/signed-storage-url`, puis le `PUT` présigné — dont le statut est lu
 *     (`verifyUpload` : un 403 ou un 5xx est un échec, plus une clé). La clé temporaire est écrite
 *     avec la pièce (`saveProgress`) dès qu'elle existe : c'est la « clé à recoller » que la
 *     finalisation envoie (`pending_attachments`) si le journal se perd, et un « Réessayer » ne
 *     renvoie pas le fichier à S3 ;
 *  2. **le journal** : `POST api/activities/{id}/logs`, `step_media_sent` avec la clé, et les trois
 *     journaux d'étape que l'ancien lot posait (`step_finished` de l'étape d'avant, `step_started` et
 *     `step_finished` de l'étape de la copie). Sa réponse est l'activité, avec ses journaux : la pièce
 *     prend l'id de son journal et l'adresse du fichier rangé. Une clé que le serveur a déjà rangée
 *     n'est pas recollée (la réponse d'un premier envoi perdue) : il rend le journal qu'il a.
 *
 * Les deux temps partent du service sans passer par `apiRequest` (la file attend une promesse, et une
 * file qui relance au démarrage n'ouvre pas la boîte « Pas de connexion » du legacy) : aucun réducteur
 * n'écoute `postActivityLogs`, rien n'est perdu. La file garde le compte qui a pris la pièce (`owner`
 * de la file commune, le compte connecté) : sous un autre compte de l'appareil, elle ne part pas, ne se
 * montre pas, et repart quand son compte revient.
 *
 * Chaque échec d'un envoi dit son étape au journal de la séance (`copy_upload_failed`) : ce journal passe par notre
 * API, il arrive donc d'un poste où Bugsnag et Amplitude sont bloqués. Hors live, il n'y a pas de journal
 * où écrire : Bugsnag seul.
 */
import moment from "moment"

import { API_URL } from "../../../data/constants/apiConstants"
import LiveEventBuffer from "../../../data/services/LiveEventBuffer"
import { activitiesService } from "../../../data/services/activitiesService"
import { vaporStore } from "../../../data/services/vaporService"
import { fetchResourceFromURI } from "../../../data/utility/fileFromURI"
import {
    createOutbox,
    createOutboxFileStore,
    type Outbox,
    type OutboxFile,
    type OutboxItem,
    type OutboxOptions,
    type OutboxSendContext,
} from "../../shared/outbox/outbox"
import { engineOutboxTelemetry } from "../../shared/outbox/outboxTelemetry"
import { currentAccountId } from "../../shared/currentAccount"
import { causeMessageOf, describeNonError, nonErrorText } from "../../shared/nonError"
import { httpStatusOf } from "../../shared/telemetry"

/** Ce que la copie rend : une page photographiée, ou l'enregistrement d'une activité orale. */
export type CopyKind = "photo" | "audio"

/** Où une pièce part, fixé au moment du geste. */
export type CopyTarget = {
    activityId: number
    kind: CopyKind
    /** L'étape de la copie, comptée à partir de 1 (celle du modèle qui porte la photo ou l'oral). */
    mediaStep: number
    /** L'étape d'avant (au moins 1) : son `step_finished`, comme l'ancien lot. */
    previousStep: number
}

/** Une pièce enregistrée : son journal, le fichier rangé, la clé de S3. */
export type CopyResult = {
    logId: number | null
    uri: string | null
    tmpKey: string
}

export type CopyFile = OutboxFile & { durationMs?: number }
export type CopyItem = OutboxItem<CopyTarget, CopyResult, CopyFile>
export type CopyOutbox = Outbox<CopyTarget, CopyResult, CopyFile>

/** La clé S3 d'une pièce, une fois le premier temps fait. */
export function tmpKeyOf(item: Pick<CopyItem, "progress" | "result">): string | null {
    const key = item.result?.tmpKey ?? item.progress?.tmpKey
    return typeof key === "string" && key !== "" ? key : null
}

/** Le type de BASE, sans paramètres : `audio/webm;codecs=opus` → `audio/webm` (le backend le lit tel quel). */
export function baseMimeType(type: string | null | undefined): string {
    return (type ?? "").split(";")[0]!.trim()
}

/** Un journal de l'activité, tel que `/logs` et `GET api/activities?with_logs=1` le servent. */
export type ApiCopyLog = {
    id?: number | null
    action?: string | null
    work_attachment?: string | null
    /** La clé S3 dont la page vient (servie depuis que `/logs` ne recolle plus une clé déjà rangée). */
    work_attachment_tmp_key?: string | null
}
type ApiLogsResponse = { data?: { data?: { activity_logs?: ApiCopyLog[] | null } | null } | null }

const now = () => moment().format("YYYY-MM-DD HH:mm:ss")

/** Le corps de `/logs` : celui de l'ancien lot (`tmpFilesFormatter` + `additionalData`). */
export function copyLogsBody(target: CopyTarget, tmpKey: string, createdAt: string = now()) {
    return {
        activity_logs: [
            {
                step: target.mediaStep,
                step_data: null,
                work_attachment: tmpKey,
                action: "step_media_sent",
                created_at: createdAt,
            },
            { action: "step_finished", step: target.previousStep, created_at: createdAt },
            { action: "step_started", step: target.mediaStep, created_at: createdAt },
            { action: "step_finished", step: target.mediaStep, created_at: createdAt },
        ],
    }
}

/**
 * Le journal de la pièce dans la réponse : celui de sa clé, que le serveur rend aussi quand il l'avait
 * déjà (la réponse d'un premier `/logs` perdue) ; à défaut, le dernier `step_media_sent` de l'activité.
 */
export function loggedMedia(response: ApiLogsResponse | null | undefined, tmpKey?: string): ApiCopyLog | null {
    const logs = (response?.data?.data?.activity_logs ?? []).filter((log) => log?.action === "step_media_sent")
    const own = tmpKey != null ? logs.find((log) => log?.work_attachment_tmp_key === tmpKey) : undefined
    if (own != null) return own
    return logs.reduce<ApiCopyLog | null>(
        (latest, log) => (latest == null || (log?.id ?? 0) > (latest.id ?? 0) ? log : latest),
        null
    )
}

/** Où l'envoi d'une pièce a cassé : le fichier relu, l'adresse signée, le `PUT`, son statut, le journal. */
export type CopyFailureStep = "read" | "signed_url" | "put" | "verify" | "logs"

export type CopyFailure = { target: CopyTarget; step: CopyFailureStep; cause: unknown; attempts: number }

export type CopySendDependencies = {
    readFile: (uri: string) => Promise<Blob>
    upload: (file: Blob, contentType: string) => Promise<{ key?: string | null }>
    postLogs: (activityId: number, body: ReturnType<typeof copyLogsBody>) => Promise<ApiLogsResponse>
    reportFailure?: (failure: CopyFailure) => void
}

export function makeSendCopyPiece(deps: CopySendDependencies) {
    return async (item: CopyItem, context: OutboxSendContext): Promise<CopyResult> => {
        const { target, file } = item
        let step: CopyFailureStep = "read"
        try {
            let tmpKey = tmpKeyOf(item)
            if (tmpKey == null) {
                const blob = await deps.readFile(file.uri)
                step = "signed_url"
                const stored = await deps.upload(blob, baseMimeType(file.mimeType) || baseMimeType(blob.type))
                if (stored?.key == null || stored.key === "") throw new Error("S3 upload returned no key")
                tmpKey = stored.key
                await context.saveProgress({ tmpKey })
            }
            step = "logs"
            const response = await deps.postLogs(target.activityId, copyLogsBody(target, tmpKey))
            const log = loggedMedia(response, tmpKey)
            return { logId: log?.id ?? null, uri: log?.work_attachment ?? null, tmpKey }
        } catch (cause) {
            // `vaporStore` dit lui-même laquelle de ses étapes a cassé.
            const tagged = (cause as { uploadStep?: CopyFailureStep } | null)?.uploadStep
            deps.reportFailure?.({ target, step: tagged ?? step, cause, attempts: item.attempts })
            throw cause
        }
    }
}

/** Au journal de la séance, tout de suite : l'élève peut quitter le live juste après son dernier essai. */
export function logFailureToLiveSession({ target, step, cause, attempts }: CopyFailure): void {
    const buffer = LiveEventBuffer.getInstance()
    if (buffer.liveSessionId == null) return
    const status = httpStatusOf(cause)
    const reason = cause instanceof Error ? causeMessageOf(cause.message) : nonErrorText(describeNonError(cause))
    buffer.record("copy_upload_failed", {
        step,
        reason: reason.slice(0, 50),
        activity_id: target.activityId,
        attempts,
        ...(status != null ? { http_status: status } : {}),
    })
    void buffer.flush()
}

export const defaultCopySendDependencies: CopySendDependencies = {
    readFile: (uri) => fetchResourceFromURI(uri),
    upload: (blob, contentType) => vaporStore(blob, { baseURL: API_URL, contentType, verifyUpload: true }),
    postLogs: (activityId, body) => activitiesService.postActivityLogs({ id: activityId }, body),
    reportFailure: logFailureToLiveSession,
}

export function createCopyOutbox(
    options: Partial<Omit<OutboxOptions<CopyTarget, CopyResult, CopyFile>, "send">> & {
        dependencies?: CopySendDependencies
    } = {}
): CopyOutbox {
    const { dependencies = defaultCopySendDependencies, ...rest } = options
    return createOutbox<CopyTarget, CopyResult, CopyFile>({
        storageKey: "student-activity-copy-outbox",
        fileStore: createOutboxFileStore<CopyFile>("activity-copy-outbox"),
        name: "activity.copy",
        errorName: "ActivityCopyError",
        telemetry: engineOutboxTelemetry("activity.copy", "student"),
        owner: currentAccountId,
        ...rest,
        send: makeSendCopyPiece(dependencies),
    })
}

let shared: CopyOutbox | null = null

/** La file de l'app : une seule, que tous les écrans d'activité lisent (créée au premier lecteur). */
export function copyOutbox(): CopyOutbox {
    shared ??= createCopyOutbox()
    return shared
}
